import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Rozpoznawanie mowy w przeglądarce (Web Speech API, pl-PL; Chrome na Androidzie i desktopie). Do Fravity OS trafia
 * tylko tekst — nagrania nie wysyłamy ani nie zapisujemy. Brak API (Firefox, część Safari), odmowa mikrofonu albo
 * błąd usługi = pole tekstowe i ręczny formularz; nic nie blokuje pracy (MVP §14, Etap 10).
 *
 * Typy lokalne (wąskie): `SpeechRecognition` nie ma w lib „DOM” TypeScriptu.
 */

export interface SpeechAlternativeLike {
  transcript: string;
}
export interface SpeechResultLike {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: SpeechAlternativeLike;
}
export interface SpeechResultEventLike {
  resultIndex: number;
  results: { readonly length: number; [index: number]: SpeechResultLike };
}
export interface SpeechErrorEventLike {
  error: string;
  message?: string;
}
export interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onstart: (() => void) | null;
  onresult: ((event: SpeechResultEventLike) => void) | null;
  onerror: ((event: SpeechErrorEventLike) => void) | null;
  onend: (() => void) | null;
}
export type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

/** Konstruktor z przeglądarki (`SpeechRecognition` albo `webkitSpeechRecognition` w Chrome); null = brak API. */
export function speechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/**
 * idle — gotowe (albo po zakończeniu), listening — słucha, unsupported — przeglądarka bez Web Speech,
 * denied — odmowa mikrofonu (osoba albo ustawienia), error — inny błąd (komunikat w `error`).
 */
export type SpeechStatus = 'idle' | 'listening' | 'unsupported' | 'denied' | 'error';

export const SPEECH_MESSAGES = {
  unsupported: 'Ta przeglądarka nie rozpoznaje mowy. Wpisz polecenie albo dodaj wpis ręcznie.',
  denied: 'Mikrofon jest zablokowany. Wpisz polecenie albo dodaj wpis ręcznie. Dostęp włączysz w ustawieniach strony.',
  noSpeech: 'Nic nie usłyszałem. Spróbuj jeszcze raz albo wpisz polecenie.',
  audioCapture: 'Nie znaleziono mikrofonu. Wpisz polecenie albo dodaj wpis ręcznie.',
  network: 'Rozpoznawanie mowy wymaga internetu. Wpisz polecenie albo spróbuj później.',
  generic: 'Nie udało się rozpoznać mowy. Wpisz polecenie albo spróbuj jeszcze raz.',
} as const;

function errorStatus(code: string): { status: SpeechStatus; message: string } | null {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return { status: 'denied', message: SPEECH_MESSAGES.denied };
    case 'no-speech':
      return { status: 'error', message: SPEECH_MESSAGES.noSpeech };
    case 'audio-capture':
      return { status: 'error', message: SPEECH_MESSAGES.audioCapture };
    case 'network':
      return { status: 'error', message: SPEECH_MESSAGES.network };
    case 'aborted':
      return null;
    default:
      return { status: 'error', message: SPEECH_MESSAGES.generic };
  }
}

export interface SpeechState {
  /** czy przeglądarka ma Web Speech API */
  supported: boolean;
  status: SpeechStatus;
  /** rozpoznany tekst (wyniki ostateczne) */
  finalText: string;
  /** tekst w trakcie rozpoznawania (zmienia się na żywo) */
  interimText: string;
  error: string | null;
  /** zaczyna słuchać; `base` = tekst, do którego dopisujemy (np. poprawiona transkrypcja przy „Mów dalej”) */
  start(base?: string): void;
  stop(): void;
  /** czyści tekst (np. „Zacznij od nowa”) */
  reset(): void;
}

const norm = (s: string) => s.toLocaleLowerCase('pl-PL').replace(/\s+/g, ' ').trim();

/**
 * Składa kolejne wyniki rozpoznawania w jeden tekst. Chrome na Androidzie w trybie ciągłym zwraca wyniki
 * narastająco („siema”, „siema dlatego”, „siema dlatego powtarza”) i to samo zdanie kilka razy — zwykłe sklejenie
 * dawało „siemaSiema dlategoSiema dlatego powtarza…” (zgłoszenie Sebastiana 9.10.2026). Wynik, który zaczyna się od
 * dotychczasowego tekstu, zastępuje go; wynik zawarty na początku tekstu jest pomijany; inny — dopisywany ze spacją.
 */
export function mergeTranscripts(parts: readonly string[]): string {
  let acc = '';
  for (const raw of parts) {
    const part = raw.replace(/\s+/g, ' ').trim();
    if (!part) continue;
    const a = norm(acc);
    const p = norm(part);
    if (!a || p.startsWith(a)) acc = part;
    else if (a.startsWith(p) || a.endsWith(p)) continue;
    else acc = `${acc} ${part}`;
  }
  return acc;
}

/** Wynik roboczy bez części, która już jest w tekście ostatecznym (Android powtarza go na początku). */
export function stripPrefix(interim: string, finals: string): string {
  const i = interim.trim();
  if (!finals) return i;
  const f = norm(finals);
  const n = norm(i);
  if (n.startsWith(f)) return i.slice(Math.min(i.length, finals.trim().length)).trim();
  return f.endsWith(n) ? '' : i;
}

export function useSpeechRecognition({ lang = 'pl-PL' }: { lang?: string } = {}): SpeechState {
  const [ctor] = useState(speechRecognitionCtor);
  const [status, setStatus] = useState<SpeechStatus>(ctor ? 'idle' : 'unsupported');
  const [finalText, setFinalText] = useState('');
  const [interimText, setInterimText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  /** tekst z poprzednich sesji słuchania (start po „Mów dalej” dopisuje) */
  const baseRef = useRef('');

  const stop = useCallback(() => {
    recRef.current?.stop();
  }, []);

  const start = useCallback(
    (base = '') => {
      if (!ctor) {
        setStatus('unsupported');
        return;
      }
      recRef.current?.abort();
      let rec: SpeechRecognitionLike;
      try {
        rec = new ctor();
      } catch {
        setStatus('unsupported');
        return;
      }
      rec.lang = lang;
      rec.continuous = true;
      rec.interimResults = true;
      rec.maxAlternatives = 1;
      setError(null);
      setInterimText('');
      baseRef.current = base.trim();
      setFinalText(baseRef.current);
      rec.onstart = () => setStatus('listening');
      rec.onresult = (event) => {
        const finals: string[] = [];
        const interims: string[] = [];
        for (let i = 0; i < event.results.length; i++) {
          const result = event.results[i];
          const text = result?.[0]?.transcript ?? '';
          if (result?.isFinal) finals.push(text);
          else interims.push(text);
        }
        const finalJoined = mergeTranscripts(finals);
        const base = baseRef.current;
        const joined = [base, finalJoined].filter(Boolean).join(' ');
        setFinalText(joined);
        setInterimText(stripPrefix(mergeTranscripts(interims), finalJoined));
      };
      rec.onerror = (event) => {
        const mapped = errorStatus(event.error);
        if (!mapped) return;
        setStatus(mapped.status);
        setError(mapped.message);
      };
      rec.onend = () => {
        setInterimText('');
        setStatus((s) => (s === 'listening' ? 'idle' : s));
        if (recRef.current === rec) recRef.current = null;
      };
      recRef.current = rec;
      setStatus('listening');
      try {
        rec.start();
      } catch {
        setStatus('error');
        setError(SPEECH_MESSAGES.generic);
      }
    },
    [ctor, lang],
  );

  const reset = useCallback(() => {
    recRef.current?.abort();
    baseRef.current = '';
    setFinalText('');
    setInterimText('');
    setError(null);
    setStatus(ctor ? 'idle' : 'unsupported');
  }, [ctor]);

  // zamknięcie widoku = koniec słuchania (mikrofon nie zostaje włączony w tle)
  useEffect(
    () => () => {
      const rec = recRef.current;
      if (rec) {
        rec.onend = null;
        rec.onresult = null;
        rec.onerror = null;
        rec.abort();
      }
    },
    [],
  );

  return { supported: Boolean(ctor), status, finalText, interimText, error, start, stop, reset };
}
